import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getGame } from '../../data/games';
import { NotFound } from '../NotFound/NotFound';
import './Game.css';

const ArrowIcon: React.FC<{ dir?: 'left' | 'right' }> = ({ dir = 'right' }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <><path d="M19 12H5" /><path d="M11 6l-6 6 6 6" /></> : <><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></>}
  </svg>
);

const ExtIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M8 7h9v9" /></svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
);

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M7 5v14l12-7z" /></svg>
);

const SteamIcon: React.FC = () => (
  <svg className="platform-icon" viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
  </svg>
);

export const Game: React.FC = () => {
  const { slug } = useParams();
  const game = getGame(slug);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    if (game) document.title = `${game.title} | Practical Media`;
  }, [game]);

  if (!game) return <NotFound />;

  const accentStyle = { ['--game-accent' as string]: game.accent } as React.CSSProperties;
  const primary = game.links.find((l) => l.primary);

  const hasTrailer = Boolean(game.youtubeId || game.heroVideo);
  const secondaryLinks = game.links.filter((l) => l !== primary);
  const titleAlt = game.subtitle ? `${game.title}: ${game.subtitle}` : game.title;
  const pad = (n: number) => String(n + 1).padStart(2, '0');

  return (
    <div className="game page" style={accentStyle}>
      <div className="container game-wrap">
        <Link to="/games" className="game-back"><ArrowIcon dir="left" />Back to games</Link>

        {/* Hero */}
        <header className="game-hero">
          <div className="game-art">
            <img src={game.heroImage} alt={`${game.title} key art`} />
          </div>
          <div className="game-hero-info">
            <p className="game-eyebrow">
              {game.studioLogo && <img className="game-studio-logo" src={game.studioLogo} alt={`${game.studio} logo`} />}
              {game.studio}
            </p>
            {game.titleLogo ? (
              <h1 className="game-title-logo">
                <img src={game.titleLogo} alt={titleAlt} />
              </h1>
            ) : (
              <h1 className="game-title">{game.title}</h1>
            )}
            {!game.titleLogo && game.subtitle && <p className="game-subtitle">{game.subtitle}</p>}
            {game.status && <p className="game-status-row"><span className="game-status">{game.status}</span></p>}
            <p className="game-tagline">{game.tagline}</p>
            <div className="game-cta">
              {primary && (
                <a href={primary.href} target="_blank" rel="noopener noreferrer" className="primary-btn game-btn">
                  <PlayIcon />{primary.label}
                </a>
              )}
              {secondaryLinks.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="btn-ghost game-ghost">
                  {l.label}
                </a>
              ))}
              {game.presskit && (
                <Link to={`/games/${game.slug}/presskit`} className="btn-ghost game-ghost">Press kit</Link>
              )}
            </div>
          </div>
        </header>

        {/* Metadata */}
        <section className="game-block game-block-tight">
          <dl className="game-meta">
            <div><dt>Developer</dt><dd>{game.developer}</dd></div>
            <div><dt>Genre</dt><dd>{game.genre}</dd></div>
            <div><dt>Platforms</dt><dd>
              {game.links.some((l) => /steampowered\.com/.test(l.href)) && <SteamIcon />}
              {game.platforms}
            </dd></div>
            {game.input && <div><dt>Input</dt><dd>{game.input}</dd></div>}
            <div><dt>Release</dt><dd>{game.releaseDate}</dd></div>
            <div><dt>Published by</dt><dd>Practical Media</dd></div>
          </dl>
        </section>

        {/* Trailer */}
        {hasTrailer && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">Trailer</span></div>
            <div className="game-video">
              {game.youtubeId ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${game.youtubeId}`}
                  title={`${game.title} trailer`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video controls autoPlay muted loop playsInline poster={game.heroImage}>
                  <source src={game.heroVideo} type="video/mp4" />
                </video>
              )}
            </div>
          </section>
        )}

        {/* About */}
        <section className="game-block">
          <div className="game-sec-head"><span className="game-sec-label">The pitch</span></div>
          <div className="game-pitch">
            {game.body.map((p, i) => <p key={i} className="body-text">{p}</p>)}
          </div>
        </section>

        {/* Gameplay pillars */}
        <section className="game-block">
          <div className="game-sec-head"><span className="game-sec-label">Gameplay</span></div>
          <div className="pillars">
            {game.pillars.map((p, i) => (
              <div key={p.title} className="pillar">
                <span className="pillar-k">{pad(i)}</span>
                <p className="pillar-title">{p.title}</p>
                <p className="pillar-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="game-block">
          <div className="game-sec-head"><span className="game-sec-label">Features</span></div>
          <ul className="game-features">
            {game.features.map((f) => (
              <li key={f}><span className="game-check"><CheckIcon /></span><span>{f}</span></li>
            ))}
          </ul>
        </section>

        {/* Gameplay clips (silent looping webm) */}
        {game.clips && game.clips.length > 0 && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">Gameplay in motion</span></div>
            <div className="clips">
              {game.clips.map((src) => (
                <div key={src} className="shot clip">
                  <video src={src} autoPlay muted loop playsInline preload="metadata" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Screenshots */}
        {game.screenshots.length > 0 && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">Screenshots</span></div>
            <div className="shots">
              {game.screenshots.map((src, i) => (
                <button key={src} className="shot" onClick={() => setLightbox(src)} aria-label={`View screenshot ${i + 1}`}>
                  <img src={src} alt={`${game.title} screenshot ${i + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Cast */}
        {game.characters && game.characters.length > 0 && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">Meet the cast</span></div>
            <div className="cast">
              {game.characters.map((c) => (
                <figure key={c.name} className="cast-card">
                  <div className="cast-portrait">
                    <img src={c.image} alt={c.name} loading="lazy" />
                  </div>
                  <figcaption>
                    <p className="cast-name">{c.name}</p>
                    {c.role && <p className="cast-role">{c.role}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Bespoke flavour (e.g. "Gods, but make them fish") */}
        {game.sections?.map((s) => (
          <section key={s.label} className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">{s.label}</span></div>
            <ul className="game-defs">
              {s.items.map((it) => (
                <li key={it.term}>
                  <span className="game-def-term">{it.term}</span>
                  {it.desc && <span className="game-def-desc"> ({it.desc})</span>}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* For fans of */}
        {game.fansOf && game.fansOf.length > 0 && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">For fans of</span></div>
            <div className="game-chips">
              {game.fansOf.map((f) =>
                f.href ? (
                  <a key={f.name} className="game-chip" href={f.href} target="_blank" rel="noopener noreferrer nofollow">
                    {f.name}<ExtIcon />
                  </a>
                ) : (
                  <span key={f.name} className="game-chip game-chip-static">{f.name}</span>
                )
              )}
            </div>
          </section>
        )}

        {/* Follow the studio */}
        {game.socials && game.socials.length > 0 && (
          <section className="game-block">
            <div className="game-sec-head"><span className="game-sec-label">Follow {game.studio}</span></div>
            <div className="game-chips">
              {game.socials.map((s) => (
                <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer nofollow" className="game-chip">
                  {s.label}<ExtIcon />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Closing CTA */}
        <section className="game-block game-foot">
          <div className="game-cta">
            {primary && (
              <a href={primary.href} target="_blank" rel="noopener noreferrer" className="primary-btn game-btn">
                <PlayIcon />{primary.label}
              </a>
            )}
            <span className="game-foot-note">Published by Practical Media</span>
            <Link to="/contact" className="game-foot-link">Submit your game<ArrowIcon /></Link>
          </div>
        </section>
      </div>

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <img src={lightbox} alt="" />
        </div>
      )}
    </div>
  );
};
